/**
 * Offline sync holding tables (Phase 7 / D-09 / D-14 / D-15).
 *
 * sync_conflicts — holding table for driver/server conflicts detected during
 *   the sync receive operation (D-09). Conflict detector (07-04) inserts here;
 *   a resolution workflow (future) reads and updates. RLS-isolated by tenant.
 *
 * sync_orphan_parking — server-side backstop for orphaned child entities that
 *   arrive before their parent canonical id is known (D-15). Reconciler (07-05)
 *   promotes PARKED → RECONCILED once the parent id is resolvable.
 *
 * Both tables:
 *   - text + CHECK per DEC-024 convention (no pgEnum — evolvable)
 *   - RLS USING tenant_id = current_setting('app.current_tenant_id')::uuid
 *   - GRANT SELECT/INSERT/UPDATE to app_user (no DELETE — append-only per §32
 *     audit trail requirement; soft-state via status column)
 *
 * Sources:
 *   - master spec §6 (permission matrix)
 *   - CONTEXT D-09 (sync_conflicts shape + conflict type codes)
 *   - CONTEXT D-14 (conflict isolation RLS requirement)
 *   - CONTEXT D-15 (orphan parking backstop)
 *   - 07-PATTERNS.md (verbatim table def template)
 */

import { check, index, jsonb, pgPolicy, pgTable, text, timestamp, uuid } from 'drizzle-orm/pg-core';
import { sql } from 'drizzle-orm';
import { appUserRole } from './enums.js';
import { tenants } from './users.js';
import { shifts } from './drivers.js';

/**
 * sync_conflicts — offline conflict holding table (D-09 / D-14).
 *
 * Populated by the sync receiver (07-04) when an incoming driver push
 * conflicts with the current server state. Both sides are stored verbatim
 * (driver_payload = full offline write payload; server_state = snapshot at
 * detection time) so a human reviewer can make a deterministic resolution
 * decision keyed on updated_at + tenant (SYNC-03 / D-17).
 *
 * status lifecycle: OPEN → RESOLVED (text + CHECK, not pgEnum — DEC-024).
 * No DELETE granted to app_user — append-only for audit integrity.
 */
export const syncConflicts = pgTable(
  'sync_conflicts',
  {
    id: uuid('id').primaryKey().defaultRandom(),
    /** NULL for a shift item (migration 0190); sync_conflicts_subject_check needs one of the two. */
    jobId: uuid('job_id'),
    /**
     * Migration 0190 — the shift a parked offline shift start or equipment change is about
     * (SHIFT_MERGED, SHIFT_OVERLAP, TRUCK_IN_USE, EQUIPMENT_INVALID, EQUIPMENT_MISSING).
     */
    shiftId: uuid('shift_id').references(() => shifts.id, { onDelete: 'cascade' }),
    tenantId: uuid('tenant_id')
      .notNull()
      .references(() => tenants.id, { onDelete: 'cascade' }),
    /** Discriminator — e.g. 'STATUS_CONFLICT', 'COMPLETION_VS_CANCELLATION' (D-09) */
    conflictType: text('conflict_type').notNull(),
    /** Driver's full offline write payload, stored verbatim (D-09 both-sides-verbatim) */
    driverPayload: jsonb('driver_payload').notNull(),
    /** Server state snapshot at detection time, stored verbatim (D-09 both-sides-verbatim) */
    serverState: jsonb('server_state').notNull(),
    /** OPEN = awaiting resolution; RESOLVED = determinstically resolved (D-09) */
    status: text('status').notNull().default('OPEN'),
    detectedAt: timestamp('detected_at', { withTimezone: true }).notNull().defaultNow(),
    /** UUID of the user or system actor who resolved the conflict; NULL while OPEN */
    resolvedBy: uuid('resolved_by'),
    resolvedAt: timestamp('resolved_at', { withTimezone: true }),
    /**
     * DEC-357 (0180) — the decision code; NULL while OPEN. KEPT_SAVED / APPLIED_PARKED /
     * SUPERSEDED for a DUPLICATE_CAPTURE, DISMISSED for every other kind.
     */
    resolution: text('resolution'),
    /** DEC-357 (0180) — the reviewer's reason for the decision, in their own words. */
    resolutionReason: text('resolution_reason'),
    /**
     * DEC-357 (0180) — the user whose push was parked. Rows parked before 0180 were
     * backfilled; for kinds other than DUPLICATE_CAPTURE that backfill is the job's
     * assigned driver at migration time (inferred, see the migration header).
     */
    driverUserId: uuid('driver_user_id'),
  },
  (t) => [
    index('sync_conflicts_job_idx').on(t.jobId),
    index('sync_conflicts_tenant_status_idx').on(t.tenantId, t.status),
    index('sync_conflicts_shift_idx')
      .on(t.shiftId)
      .where(sql`shift_id IS NOT NULL`),
    check('sync_conflicts_subject_check', sql`job_id IS NOT NULL OR shift_id IS NOT NULL`),
    // D-09 allowlist: text + CHECK per DEC-024 convention
    check('sync_conflicts_status_check', sql`status IN ('OPEN','RESOLVED')`),
    // DEC-357 (0180) — the decision codes.
    check(
      'sync_conflicts_resolution_check',
      sql`resolution IS NULL OR resolution IN ('KEPT_SAVED','APPLIED_PARKED','SUPERSEDED','DISMISSED')`,
    ),
    // T-07-04 — cross-tenant read guard. Same pattern as tenant_users (users.ts lines 225-230).
    pgPolicy('sync_conflicts_isolation', {
      as: 'permissive',
      to: appUserRole,
      for: 'all',
      using: sql`tenant_id = current_setting('app.current_tenant_id')::uuid`,
    }),
  ],
);

export type SyncConflict = typeof syncConflicts.$inferSelect;
export type NewSyncConflict = typeof syncConflicts.$inferInsert;

/**
 * sync_orphan_parking — orphaned entity backstop (D-05 / D-15).
 *
 * When the sync receiver receives a child entity (e.g. a haul_event) whose
 * parent (e.g. a job) does not yet have a canonical id on the server, the
 * child is PARKED here. The orphan reconciler (07-05) periodically scans
 * PARKED rows whose parent_canonical_id is now resolvable and re-drives them
 * through the normal receiver path.
 *
 * job_id is nullable — the orphan may arrive before the parent job is known
 * (the canonical job id is what is being resolved). parent_temp_id stores the
 * client-side temp key so the resolver can JOIN on the sync_key map.
 *
 * status lifecycle: PARKED → RECONCILED | ABANDONED (text + CHECK, DEC-024).
 */
export const syncOrphanParking = pgTable(
  'sync_orphan_parking',
  {
    id: uuid('id').primaryKey().defaultRandom(),
    /** Nullable — orphan may arrive before the parent job canonical id is known */
    jobId: uuid('job_id'),
    tenantId: uuid('tenant_id')
      .notNull()
      .references(() => tenants.id, { onDelete: 'cascade' }),
    /** Drizzle type discriminator — e.g. 'haul_event', 'job_event' (D-15) */
    entityType: text('entity_type').notNull(),
    /** Client-side temp key of the parent entity (D-05 SyncKey → canonical ID map) */
    parentTempId: text('parent_temp_id'),
    /** Server canonical id once the parent is resolvable; NULL until reconciled */
    parentCanonicalId: uuid('parent_canonical_id'),
    /** Full entity payload from the driver's push, stored verbatim */
    payload: jsonb('payload').notNull(),
    /** PARKED = waiting for parent; RECONCILED = replayed successfully; ABANDONED = gave up */
    status: text('status').notNull().default('PARKED'),
    parkedAt: timestamp('parked_at', { withTimezone: true }).notNull().defaultNow(),
    reconciledAt: timestamp('reconciled_at', { withTimezone: true }),
  },
  (t) => [
    index('sync_orphan_parking_parent_idx').on(t.parentTempId),
    index('sync_orphan_parking_canonical_idx').on(t.parentCanonicalId),
    // D-15 status allowlist: text + CHECK per DEC-024 convention
    check('sync_orphan_parking_status_check', sql`status IN ('PARKED','RECONCILED','ABANDONED')`),
    // T-07-04 — same RLS guard as sync_conflicts
    pgPolicy('sync_orphan_parking_isolation', {
      as: 'permissive',
      to: appUserRole,
      for: 'all',
      using: sql`tenant_id = current_setting('app.current_tenant_id')::uuid`,
    }),
  ],
);

export type SyncOrphanParking = typeof syncOrphanParking.$inferSelect;
export type NewSyncOrphanParking = typeof syncOrphanParking.$inferInsert;
